import React from 'react';
import dayjs from 'libs/dayjs';
import TemplateRenderer from 'PDF/TemplateEngine/TemplateRenderer';

// Disclaimer Res. 38 (constante, no editable). El reporte de visita es
// evidencia de servicio al cliente, no registro de asistencia/jornada.
export const RES38_DISCLAIMER =
  'Este documento es un informe de servicio técnico. No constituye registro de asistencia laboral ni sustituye los mecanismos de control de jornada exigidos por el Código del Trabajo.';

const fmtFecha = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');

const fmtGps = (lat, lng) =>
  lat != null && lng != null
    ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
    : 'No registrada';

const shortHash = (h) => (h ? `${h.slice(0, 16)}…${h.slice(-4)}` : null);

const clienteLabel = (reporte, clienteNombre) =>
  reporte.cliente_no_registrado?.nombre ||
  clienteNombre ||
  (reporte.id_cliente ? `Cliente #${reporte.id_cliente}` : '—');

// Arma un documento lowcode ad-hoc a partir del reporte: bloque de metadatos
// (data-grid) + las secciones que el técnico construyó (texto, foto, checklist,
// firma) + disclaimer Res. 38 con línea de hash. Delega el render a
// TemplateRenderer (reusa los section types existentes).
function ReporteVisitaDocument({ reporte, clienteNombre, data: extraData, config }) {
  if (!reporte) return null;

  const meta = {
    rv_cliente: clienteLabel(reporte, clienteNombre),
    rv_tipo: reporte.tipo,
    rv_tecnico: reporte.created_by_rut,
    rv_apertura: fmtFecha(reporte.createdAt),
    rv_cierre: fmtFecha(reporte.finalizado_at),
    rv_ubic_apertura: fmtGps(reporte.gps_apertura_lat, reporte.gps_apertura_lng),
    rv_ubic_cierre: fmtGps(reporte.gps_cierre_lat, reporte.gps_cierre_lng),
  };

  const metaSection = {
    id: 'rv-meta',
    type: 'data-grid',
    title: 'Datos de la visita',
    visible: true,
    config: {
      fields: [
        { key: 'rv_cliente', label: 'Cliente', visible: true },
        { key: 'rv_tipo', label: 'Tipo de visita', visible: true },
        { key: 'rv_tecnico', label: 'Técnico responsable', visible: true },
        { key: 'rv_apertura', label: 'Apertura', visible: true },
        { key: 'rv_cierre', label: 'Cierre', visible: true },
        { key: 'rv_ubic_apertura', label: 'Ubicación al abrir', visible: true },
        { key: 'rv_ubic_cierre', label: 'Ubicación al cerrar', visible: true },
      ],
    },
  };

  // Los bloques `signatures` del editor guardan la key S3 en
  // config.specialistSignatureKey, pero SignaturesSection (PDF) lee la imagen de
  // `data.specialist_signature` (resuelta upstream en Dowload). Normalizamos el
  // config para que la sección renderice al especialista.
  const secciones = (Array.isArray(reporte.secciones) ? reporte.secciones : []).map((s) =>
    s.type === 'signatures'
      ? {
          ...s,
          config: { showSpecialist: true, showReceptionist: false, specialistLabel: 'Técnico' },
        }
      : s
  );

  const template = {
    id: 'reporte-visita',
    name: reporte.titulo || 'Reporte de visita',
    logo: config?.logo ?? null,
    sections: [metaSection, ...secciones],
  };

  // specialist_name/specialist_signature alimentan SignaturesSection. La firma
  // resuelta (URL) llega por extraData desde Dowload.
  const data = { specialist_name: reporte.created_by_rut, ...meta, ...(extraData || {}) };

  const footerDisclaimer = {
    text: RES38_DISCLAIMER,
    hashLine: reporte.integrity_hash
      ? `Hash de integridad SHA-256: ${shortHash(reporte.integrity_hash)}`
      : null,
  };

  return (
    <TemplateRenderer
      template={template}
      data={data}
      config={config || {}}
      footerDisclaimer={footerDisclaimer}
    />
  );
}

export default ReporteVisitaDocument;
